import { Injectable } from '@angular/core';
import { FormArray, FormBuilder, FormControl } from '@angular/forms';
import { HttpEvent, HttpEventType } from '@angular/common/http';
import { BehaviorSubject, Observable } from 'rxjs';

import { HashService } from '../../shared/services/hash.service';
import { TranslationService } from '../../shared/services/translation.service';
import { ERROR_MIME, MIME_TYPE, ERROR_SIZE, ERROR_FILE_NAME } from '../models/fastlane-list';
import { FastLaneRequestAttachment, ErrorResponse, FastLaneRequestAttachmentForm } from '../models/fastlane.model';

import { FastLaneService } from './fastlane.service';

@Injectable({
	providedIn: 'root'
})
export class AttachmentService {
	uploadAttachmentObs: Observable<number>;
	private readonly MAX_FILE_ALLOWED = 3;
	private readonly uploadAttachmentSubject = new BehaviorSubject(<number> 0);
	constructor(
		private readonly hashService: HashService,
		private readonly fastlaneService: FastLaneService,
		private readonly fb: FormBuilder,
		private readonly translationService: TranslationService,
	) {
		this.uploadAttachmentObs = this.uploadAttachmentSubject.asObservable();
	}

	checkMaxFileLimit(files: File[], attachmentFormGroups: FormArray): boolean {
		// Get existing file names
		const existingFileNames = new Set(
			attachmentFormGroups.controls.map(control =>
				(<FastLaneRequestAttachmentForm>control.value).fastlaneAttachment.fileName)
		);

		// Count only files that don't already exist (new files)
		const newFilesCount = files.filter(file => !existingFileNames.has(file.name)).length;

		// Check if total (existing + new) exceeds limit
		return (attachmentFormGroups.length + newFilesCount) <= this.MAX_FILE_ALLOWED;
	}

	async uploadFiles(files: File[], attachmentFormGroups: FormArray, attachmentErrorControl: FormControl):
	Promise<void> {
		attachmentErrorControl.setValue('');
		if (!this.checkMaxFileLimit(files, attachmentFormGroups)) {
			attachmentErrorControl.setValue(this.translationService.getTR('upload.file.maxitem.error.message'));
			return;
		}
		this.uploadAttachmentSubject.next(files.length);
		for (const file of files) {
			const hash = await this.getFileHash(file);
			let blockUpload = false;
			for (let i = 0; i < attachmentFormGroups.length; i++) {
				const currentAttachment = (<FastLaneRequestAttachmentForm>attachmentFormGroups
					.controls[i].value).fastlaneAttachment;
				if (currentAttachment.fileName === file.name) {
					if (currentAttachment.hash === hash) {
						// same file name and hash value - ignore upload
						blockUpload = true;
					} else {
						// same file name different content - remove old file and upload new one
						this.removeAttachment(currentAttachment, attachmentFormGroups);
					}
					break;
				}
			}
			if (blockUpload) {
				this.uploadAttachmentSubject.next(-1);
				continue;
			}
			const localUrl = URL.createObjectURL(file);

			const attachment: FastLaneRequestAttachment = {
				fileName: file.name,
				fileSize: file.size,
				type: file.type,
				localUrl,
				localFile: file,
				chronicleId: '',
				hash,
			};

			const attachmentFormGroup = this.fb.group({ fastlaneAttachment: attachment });
			attachmentFormGroups.push(attachmentFormGroup);
			await new Promise<void>((resolve, reject) => {
				attachment.uploadSub = this.fastlaneService.uploadAttachment(file, hash).subscribe({
					next: event => {
						this.handleUploadEvent(event, attachment);
					},
					complete: () => {
						this.handleUploadComplete(attachment);
						resolve();
					},
					error: (errObj: unknown) => {
						this.handleUploadError(errObj, attachment, attachmentFormGroups, attachmentErrorControl);
						reject(errObj instanceof Error ? errObj : new Error(String(errObj)));
					}
				});
			});
		}
	}

	removeAttachment(attachment: FastLaneRequestAttachment, attachmentFormGroups: FormArray): void {
		const index = attachmentFormGroups.controls.findIndex(control =>
			(<FastLaneRequestAttachmentForm>control.value).fastlaneAttachment.fileName === attachment.fileName);

		if (index !== -1) {
			const currentAttachment = (<FastLaneRequestAttachmentForm>
				attachmentFormGroups.controls[index].value).fastlaneAttachment;

			if (attachment.chronicleId) {
				attachment.uploadSub = this.fastlaneService.deleteAttachment(
					String(currentAttachment.chronicleId)
				).subscribe({
					complete: () => {
						attachment.uploadSub = undefined;
					},
					error: (err: unknown) => {
						console.error('error while removing file', err);
						currentAttachment.uploadSub = undefined;
					}
				});
			} else if (currentAttachment.uploadSub) {
				currentAttachment.uploadSub.unsubscribe();
				currentAttachment.uploadSub = undefined;
			}

			attachmentFormGroups.removeAt(index);
		}
	}

	private handleUploadEvent(event: HttpEvent<string>, attachment: FastLaneRequestAttachment): void {
		switch (event.type) {
		case HttpEventType.Sent:
			attachment.progress = 0;
			break;
		case HttpEventType.UploadProgress:
			if (event.total) {
				attachment.progress = Math.round((event.loaded / event.total) * 100);
			}
			break;
		case HttpEventType.ResponseHeader:
			break;
		case HttpEventType.Response:
			if (event.body) {
				attachment.chronicleId = event.body;
				attachment.progress = 100;
				attachment.localFile = undefined;
			}
			break;
		default:
			break;
		}
	}

	private handleUploadComplete(attachment: FastLaneRequestAttachment): void {
		this.uploadAttachmentSubject.next(-1);
		attachment.uploadSub = undefined;
	}

	private handleUploadError(
		errObj: unknown,
		attachment: FastLaneRequestAttachment,
		attachmentFormGroups: FormArray,
		attachmentErrorControl: FormControl
	): void {
		this.handleError(<ErrorResponse>errObj, attachment, attachmentErrorControl);
		attachment.uploadSub = undefined;
		this.removeAttachment(attachment, attachmentFormGroups);
		this.uploadAttachmentSubject.next(-1);
	}

	private async getFileHash(file: File): Promise<string> {
		return this.hashService.readSHA1(file);
	}

	private handleError(
		errorResponse: ErrorResponse,
		attachment: FastLaneRequestAttachment,
		attachmentErrorControl: FormControl
	): void {
		const jsonObject = <{ code: string }> JSON.parse(errorResponse.error);
		let errorMsg;

		switch (jsonObject.code) {
		case ERROR_MIME:
			errorMsg = this.translationService.getTR('upload.file.mime.type.error.message');
			errorMsg = attachment.type ? errorMsg.replace(MIME_TYPE, attachment.type) : errorMsg.split('.')[0];
			break;
		case ERROR_SIZE:
			errorMsg = this.translationService.getTR('upload.file.size.error.message');
			break;
		case ERROR_FILE_NAME:
			errorMsg = this.translationService.getTR('upload.file.name.error.message');
			break;
		default:
			errorMsg = this.translationService.getTR('upload.file.error.message');
		}

		attachmentErrorControl.setValue(errorMsg);
		console.error('error uploading file', errorResponse);
	}
}
